'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { Service } from '../../types/services';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import { useState } from 'react';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';
import { Heading } from '../ui/heading';
import { PriceOfferDialog } from '../ui/price-offer-dialog';
import { Button } from '../ui/button';
import { useDynamicSheetTop } from '../../hooks/use-dynamic-sheet-top';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  services: Service[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const { isOpen, setIsOpen } = useDisclosure();
  const [isOpenLevel2, setIsOpenLevel2] = useState(false);

  const dynamicTop = useDynamicSheetTop();

  const sheetContentClass =
    'fixed flex w-full max-w-[800px] flex-col gap-6 border-none bg-white px-5 py-8 shadow-none ' +
    'top-[92px] sm:top-[108px] md:top-[125px] lg:top-[133px] ' +
    'h-[calc(100vh-92px)] sm:h-[calc(100vh-108px)] md:h-[calc(100vh-125px)] lg:h-[calc(100vh-133px)] ' +
    'overflow-y-auto overscroll-contain hide-scroll-bar';

  const handleCloseAll = () => {
    setIsOpenLevel2(false);
    setIsOpen(false);
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger aria-label="Menu"
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          <Image
            src={
              isOpen ? '/images/icons/sidebar/close-square.svg' : '/images/icons/sidebar/menu.svg'
            }
            alt={isOpen ? 'Zatvoriť menu' : 'Otvoriť menu'}
            width={24}
            height={24}
          />
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          hideOverlay
          side={props.side}
          style={{
            top: dynamicTop + 35,
            height: `calc(100vh - ${dynamicTop + 35}px)`,
          }}
          closeIcon={{
            icon: 'hidden',
            wrapper: 'hidden',
          }}
          className={cn(
            'hide-scroll-bar fixed flex w-full max-w-[800px] flex-col gap-6 overflow-y-auto overscroll-contain border-none bg-white px-5 py-8',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          {props.menu?.map(item => {
            const itemClass = 'text-2xl font-normal text-black';
            const showServices = item.name === 'Naše služby';

            if (showServices) {
              return (
                <Sheet
                  key={item.name}
                  open={isOpenLevel2}
                  onOpenChange={open => {
                    setIsOpenLevel2(open);
                  }}
                >
                  <SheetTrigger
                    className={cn(itemClass, 'flex w-full items-center justify-between gap-4')}
                  >
                    {item.name} <GoArrowRight size={24} />
                  </SheetTrigger>
                  <SheetContent
                    aria-describedby={undefined}
                    hideOverlay
                    closeIcon={{ icon: 'hidden', wrapper: 'hidden' }}
                    className={cn(sheetContentClass)}
                    style={{
                      top: dynamicTop + 35,
                      height: `calc(100vh - ${dynamicTop + 35}px)`,
                    }}
                  >
                    <SheetTitle className="sr-only">{item.name}</SheetTitle>
                    <button
                      type="button"
                      onClick={() => setIsOpenLevel2(false)}
                      className="flex w-full cursor-pointer items-center justify-start gap-4 transition-all hover:opacity-75"
                    >
                      <GoArrowLeft size={24} /> <Heading tag="h5" semanticTag="span" title={t('text1345')} />
                    </button>
                    <div className="flex w-full flex-col items-start justify-start gap-4">
                      {props.services.map(service => (
                        <div className="w-full" key={service.slug} onClick={handleCloseAll}>
                          <ServiceCard service={service} />
                        </div>
                      ))}
                      <div className="relative z-10 flex w-full flex-row items-center justify-between gap-4 rounded-[8px] bg-[#061E72] p-4 sm:max-w-[420px] sm:p-6 lg:gap-8 lg:p-8">
                        <Heading
                          title="Chcem cenovú ponuku na mieru"
                          tag="h4"
                          className="w-full max-w-[250px] text-balance text-white sm:max-w-[275px]"
                        />
                        <PriceOfferDialog>
                          <Button aria-label="Chcem cenovú ponuku na mieru" variant={'white'} size={'icon'} className="rounded-full">
                            <GoArrowRight size={20} />
                          </Button>
                        </PriceOfferDialog>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>
              );
            }

            return (
              <SheetClose key={item.name} asChild>
                <Link prefetchOnHover
                  href={getSafeHref(item?.path ?? '#')}
                  className={cn(itemClass)}
                >
                  {item.name}
                </Link>
              </SheetClose>
            );
          })}
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;

function ServiceCard({ service }: { service: Service }) {
  return (
    <Link prefetchOnHover
      href={service.slug}
      className="col-span-1 flex w-full cursor-pointer items-center justify-between rounded-[8px] border border-border bg-white px-4 py-4 transition-all hover:bg-neutral-100 sm:px-4 md:p-6 xl:p-8"
    >
      <Heading tag="h5" title={service.name} />
      <Image
        src={service.image.url}
        alt={resolveImageAlt(service?.image, service?.name)}
        width={0}
        height={0}
        className="max-h-10 min-h-10 min-w-10 max-w-10 object-contain md:max-h-12 md:min-h-12 md:min-w-12 md:max-w-12"
        sizes="100vw"
      />
    </Link>
  );
}
